import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Trophy } from "lucide-react";
import Nav from "../landing-pages/home-two/components/nav";
import Footer2 from "../landing-pages/home-two/components/footer2";
import { MTN_YELLOW } from "../../constants/Colors";
import { useHideEbanqoWidget } from "../../hooks/useHideEbanqoWidget";

/**
 * Games hub. A hub from the start rather than a single game route (Ken,
 * 2026-09-04), so the other two games slot in without moving anything.
 *
 * Copy rule, and it is not optional: these are games, and nothing here claims
 * a cognitive or medical benefit. Lumosity settled with the FTC in 2016 for
 * exactly that kind of claim, and we carry a health brand.
 */

interface GameEntry {
  slug: string;
  name: string;
  tagline: string;
  /** What the player actually does — plain language, no jargon. */
  blurb: string;
  /** CSS-only cover art, so the hub costs nothing extra to load. */
  cover: string;
  ready: boolean;
  /** Throwaway prototype route, when one exists. Shown only to us — see
   *  showPrototypes below. */
  previewPath?: string;
}

const GAMES: GameEntry[] = [
  {
    slug: "kae-kente",
    name: "Kae Kente",
    tagline: "Remember the weave",
    blurb:
      "A kente strip weaves itself one band at a time. Watch the colours, then tap them back in order. Each round adds a band.",
    cover:
      "repeating-linear-gradient(90deg, #FFCC08 0 14px, #111 14px 18px, #1B7A3E 18px 32px, #111 32px 36px, #C8102E 36px 50px, #111 50px 54px)",
    ready: true,
  },
  {
    slug: "dose-dash",
    name: "Dose Dash",
    tagline: "Right thing, right time",
    blurb:
      "A day passes and slots come due. Take the tablet being asked for — not the water, not the tea — before the sun leaves it. And it keeps changing.",
    cover: "linear-gradient(160deg, #0B2447 0%, #19376D 45%, #FFCC08 100%)",
    ready: true,
  },
  {
    slug: "makola",
    name: "Makola Market Plate",
    tagline: "Build the plate",
    blurb:
      "The stall rolls past — kontomire, garden eggs, tilapia, waakye. Take only what the round asks for, and later build a whole plate.",
    cover: "linear-gradient(160deg, #1B7A3E 0%, #7BB661 50%, #FFCC08 100%)",
    ready: true,
  },
];

export default function Play() {
  // The prototype links are for us, not for visitors: a spike has no scoring,
  // no rounds and no lives, so a player who found one would think the game
  // was broken. Same flag that already gates the chat's dev reset, so it is
  // on locally and off in a real build.
  const showPrototypes = process.env.REACT_APP_SHOW_DEV_RESET === "true";

  // Kept off the games pages so its launcher does not sit over the cards and
  // the boards (Ken, 2026-09-04). Restored on the way out — Ebanqo stays
  // maintained everywhere else.
  useHideEbanqoWidget("games-hub");

  return (
    <div className="flex min-h-screen w-full flex-col bg-neutral-50">
      <Nav />

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-10 sm:px-6">
        <span
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-bold tracking-wider text-black"
          style={{ backgroundColor: MTN_YELLOW }}
        >
          <Sparkles className="h-3 w-3" strokeWidth={2.5} />
          GAMES
        </span>

        <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
          <h1 className="m-0 text-3xl font-bold text-slate-900 sm:text-4xl">
            A quick game, Ghanaian style
          </h1>
          <Link
            to="/play/leaderboard"
            className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-black transition-colors hover:brightness-95"
            style={{ backgroundColor: MTN_YELLOW }}
          >
            <Trophy className="h-4 w-4" strokeWidth={2.5} />
            Leaderboard
          </Link>
        </div>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
          Short games built around things you already know — kente, the market,
          the shape of a day. A round takes about a minute. Free to play, no
          sign-up.
        </p>

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {GAMES.map((game) => (
            <article
              key={game.slug}
              className="flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200"
            >
              <div
                className="h-24 w-full"
                style={{ backgroundImage: game.cover }}
                aria-hidden
              />
              <div className="flex flex-1 flex-col gap-2 p-4">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="m-0 text-base font-bold text-slate-900">
                    {game.name}
                  </h2>
                  {!game.ready && (
                    <span className="shrink-0 rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      In build
                    </span>
                  )}
                </div>
                <p className="m-0 text-xs font-medium text-slate-500">
                  {game.tagline}
                </p>
                <p className="m-0 flex-1 text-xs leading-relaxed text-slate-600">
                  {game.blurb}
                </p>

                {game.ready ? (
                  <Link
                    to={`/play/${game.slug}`}
                    className="mt-2 block w-full rounded-lg px-4 py-2 text-center text-sm font-semibold text-black transition-colors hover:brightness-95"
                    style={{ backgroundColor: MTN_YELLOW }}
                  >
                    Play
                  </Link>
                ) : (
                  <div className="mt-2 space-y-1.5">
                    <button
                      type="button"
                      disabled
                      className="w-full cursor-not-allowed rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-400"
                    >
                      Coming soon
                    </button>
                    {showPrototypes && game.previewPath && (
                      <Link
                        to={game.previewPath}
                        className="block text-center text-[11px] font-semibold text-slate-500 underline"
                      >
                        Open the prototype
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>

        <p className="mt-8 text-xs text-slate-500">
          Games for fun, not treatment. For anything about your health, speak
          to a healthcare professional.
        </p>
      </main>

      <Footer2 />
    </div>
  );
}
